// client/src/pages/DataAnalytics/components/SymptomCorrelation.js
// Visar progressiv symptom-kedja länk-för-länk där varje länk anger andel
// rapporter som även har nästa symptom (ex. "Diarré → 73% har Illamående").
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import API_CONFIG from '../../../config/apiConfig';
import { ALL_SYMPTOMS } from '../../../components/shared/constants/symptoms';
const SymptomCorrelation = () => {
  const { t } = useTranslation();

  // ✅ Symptom translation helper
  const tSymptom = (name) => t(`symptoms.${name}`, name);

  // Chain state
  const [chain, setChain] = useState([]);
  const [links, setLinks] = useState([]);          // Link-by-link data
  const [lastSymptom, setLastSymptom] = useState(null);  // { name, count }
  const [suggestions, setSuggestions] = useState([]);
  
  // Filter state
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedWeek, setSelectedWeek] = useState('');
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const cities = ['', 'Malmö', 'Stockholm', 'Göteborg'];
  const weeks = ['', ...Array.from({ length: 14 }, (_, i) => 40 + i)]; // 40-53

  // Fetch correlations from API
  const fetchCorrelations = useCallback(async (symptoms) => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      params.append('symptoms', symptoms.join(','));
      if (selectedCity) params.append('city', selectedCity);
      if (selectedWeek) params.append('week', selectedWeek);
      params.append('limit', '3');
      
      const url = `${API_CONFIG.baseUrl}/health-data/symptom-correlation?${params}`;
      console.log('🔗 Fetching link-by-link correlations:', url);
      
      const response = await fetch(url);
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'API request failed');
      }
      
      console.log('✅ Link-by-link result:', result.data);
      
      // Use new structure directly (no fallback)
      setLinks(result.data.links);
      setLastSymptom(result.data.lastSymptom);
      setSuggestions(result.data.nextSuggestions);
      
    } catch (err) {
      console.error('❌ Correlation error:', err);
      setError(err.message);
      setLinks([]);
      setLastSymptom(null);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCity, selectedWeek]);

  // Start chain with first symptom
  const handleStartChain = useCallback((symptom) => {
    setChain([symptom]);
    fetchCorrelations([symptom]);
  }, [fetchCorrelations]);

  // Add symptom to chain
  const handleAddToChain = useCallback((symptom) => {
    const newChain = [...chain, symptom];
    setChain(newChain);
    fetchCorrelations(newChain);
  }, [chain, fetchCorrelations]);

  // Remove last symptom from chain
  const handleRemoveLast = useCallback(() => {
    if (chain.length <= 1) {
      setChain([]);
      setLinks([]);
      setLastSymptom(null);
      setSuggestions([]);
    } else {
      const newChain = chain.slice(0, -1);
      setChain(newChain);
      fetchCorrelations(newChain);
    }
  }, [chain, fetchCorrelations]);

  // Reset everything
  const handleReset = useCallback(() => {
    setChain([]);
    setLinks([]);
    setLastSymptom(null);
    setSuggestions([]);
    setError(null);
  }, []);

  // Track if we should refetch due to filter change
  const prevFiltersRef = React.useRef({ city: selectedCity, week: selectedWeek });
  
  // Auto-refresh when filters change and chain exists
  React.useEffect(() => {
    const filtersChanged = 
      prevFiltersRef.current.city !== selectedCity || 
      prevFiltersRef.current.week !== selectedWeek;
    
    if (filtersChanged && chain.length > 0) {
      fetchCorrelations(chain);
    }
    
    prevFiltersRef.current = { city: selectedCity, week: selectedWeek };
  }, [selectedCity, selectedWeek, chain, fetchCorrelations]);

  // Get sample size warning level
  const getSampleWarning = (n) => {
    if (n < 10) return { level: 'critical', text: t('correlation.sampleCritical'), color: '#ef4444' };
    if (n < 50) return { level: 'warning', text: t('correlation.sampleWarning'), color: '#f97316' };
    if (n < 100) return { level: 'caution', text: t('correlation.sampleCaution'), color: '#eab308' };
    return null;
  };

  // Helper to get link for a specific position in chain
  const getLinkForIndex = (index) => {
    return links.find(link => link.from === chain[index] && link.to === chain[index + 1]);
  };

  // Get the count for first symptom (from first link if exists)
  const getFirstSymptomCount = () => {
    if (links.length > 0) {
      return links[0].fromCount;
    }
    if (lastSymptom && chain.length === 1) {
      return lastSymptom.count;
    }
    return null;
  };

  const lastSymptomWarning = lastSymptom ? getSampleWarning(lastSymptom.count) : null;

  return (
    <div style={{
      backgroundColor: '#1a1a2e',
      borderRadius: '12px',
      padding: '24px',
      color: '#e2e8f0'
    }}>
      <h2 style={{ margin: '0 0 8px 0', color: '#fff' }}>
        🔗 {t('correlation.title')}
      </h2>
      <p style={{ margin: '0 0 20px 0', color: '#94a3b8', fontSize: '14px' }}>
        {t('correlation.subtitle')}
      </p>

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: '16px',
        marginBottom: '20px',
        flexWrap: 'wrap'
      }}>
        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#94a3b8' }}>
            {t('correlation.filterCity')}
          </label>
          <select
            value={selectedCity}
            onChange={(e) => setSelectedCity(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #334155',
              backgroundColor: '#0f172a',
              color: '#e2e8f0',
              minWidth: '150px'
            }}
          >
            <option value="">{t('correlation.allSweden')}</option>
            {cities.filter(c => c).map(city => (
              <option key={city} value={city}>{city}</option>
            ))}
          </select>
        </div>
        
        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#94a3b8' }}>
            {t('correlation.filterWeek')}
          </label>
          <select
            value={selectedWeek}
            onChange={(e) => setSelectedWeek(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #334155',
              backgroundColor: '#0f172a',
              color: '#e2e8f0',
              minWidth: '120px'
            }}
          >
            <option value="">{t('correlation.allWeeks')}</option>
            {weeks.filter(w => w).map(week => (
              <option key={week} value={week}>{t('correlation.weekOption', { week })}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Main content - two columns */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: chain.length > 0 ? '1fr 1fr' : '1fr',
        gap: '24px'
      }}>
        
        {/* Left column: Chain display or symptom picker */}
        <div>
          {chain.length === 0 ? (
            <div>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#cbd5e1' }}>
                {t('correlation.selectStart')}
              </h3>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                maxHeight: '300px',
                overflowY: 'auto',
                padding: '12px',
                backgroundColor: '#0f172a',
                borderRadius: '8px',
                border: '1px solid #334155'
              }}>
                {ALL_SYMPTOMS.map(symptom => (
                  <button
                    key={symptom}
                    onClick={() => handleStartChain(symptom)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#1e293b',
                      color: '#e2e8f0',
                      border: '1px solid #475569',
                      borderRadius: '16px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = '#334155';
                      e.target.style.borderColor = '#60a5fa';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = '#1e293b';
                      e.target.style.borderColor = '#475569';
                    }}
                  >
                    {tSymptom(symptom)}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '12px'
              }}>
                <h3 style={{ margin: 0, fontSize: '16px', color: '#cbd5e1' }}>
                  {t('correlation.yourChain')}
                </h3>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={handleRemoveLast}
                    style={{
                      padding: '4px 10px',
                      backgroundColor: '#374151',
                      color: '#d1d5db',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    ← {t('correlation.removeLast')}
                  </button>
                  <button
                    onClick={handleReset}
                    style={{
                      padding: '4px 10px',
                      backgroundColor: '#7f1d1d',
                      color: '#fecaca',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    {t('correlation.reset')}
                  </button>
                </div>
              </div>
              
              {/* Chain visualization with link strengths */}
              <div style={{
                backgroundColor: '#0f172a',
                borderRadius: '8px',
                padding: '16px',
                border: '1px solid #334155'
              }}>
                {chain.map((symptom, index) => {
                  const link = index < chain.length - 1 ? getLinkForIndex(index) : null;
                  const isFirst = index === 0;
                  const firstCount = isFirst ? getFirstSymptomCount() : null;
                  
                  return (
                    <div key={index}>
                      {/* Symptom box */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px 16px',
                        backgroundColor: isFirst ? '#1e3a5f' : '#1e293b',
                        borderRadius: '8px',
                        border: isFirst ? '2px solid #3b82f6' : '1px solid #475569'
                      }}>
                        <span style={{
                          backgroundColor: isFirst ? '#3b82f6' : '#6b7280',
                          color: '#fff',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: 'bold'
                        }}>
                          {index + 1}
                        </span>
                        <span style={{ fontSize: '15px', fontWeight: '500', flex: 1 }}>
                          {tSymptom(symptom)}
                        </span>
                        {/* Show count for first symptom */}
                        {isFirst && firstCount && (
                          <span style={{ 
                            fontSize: '12px', 
                            color: '#94a3b8',
                            backgroundColor: '#1e293b',
                            padding: '2px 8px',
                            borderRadius: '4px'
                          }}>
                            {t('correlation.reports', { count: firstCount.toLocaleString() })}
                          </span>
                        )}
                      </div>
                      
                      {/* Link arrow with percentage (if not last) */}
                      {index < chain.length - 1 && (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '8px 0',
                          gap: '8px'
                        }}>
                          {link ? (
                            <>
                              <span style={{ color: '#6b7280', fontSize: '16px' }}>↓</span>
                              <span style={{
                                backgroundColor: link.percentage >= 50 ? '#16a34a' : 
                                                 link.percentage >= 25 ? '#ca8a04' : '#dc2626',
                                color: '#fff',
                                padding: '4px 12px',
                                borderRadius: '12px',
                                fontSize: '13px',
                                fontWeight: 'bold'
                              }}>
                                {link.percentage}%
                              </span>
                              <span style={{ 
                                color: '#64748b', 
                                fontSize: '11px' 
                              }}>
                                ({t('correlation.ofTotal', { part: link.toCount.toLocaleString(), total: link.fromCount.toLocaleString() })})
                              </span>
                            </>
                          ) : (
                            <span style={{ color: '#6b7280', fontSize: '16px' }}>↓</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                
                {/* Last symptom sample info */}
                {lastSymptom && (
                  <div style={{
                    marginTop: '16px',
                    padding: '10px 12px',
                    backgroundColor: lastSymptomWarning ? `${lastSymptomWarning.color}20` : '#16a34a20',
                    borderRadius: '6px',
                    borderLeft: `3px solid ${lastSymptomWarning ? lastSymptomWarning.color : '#16a34a'}`
                  }}>
                    <div style={{ fontSize: '13px', color: '#e2e8f0' }}>
                      <strong>{t('correlation.sampleBasis')}</strong> {t('correlation.reportsWithSymptom', { count: lastSymptom.count.toLocaleString(), symptom: tSymptom(lastSymptom.name) })}
                    </div>
                    {lastSymptomWarning && (
                      <div style={{ fontSize: '12px', color: lastSymptomWarning.color, marginTop: '4px' }}>
                        ⚠️ {lastSymptomWarning.text}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right column: Suggestions */}
        {chain.length > 0 && (
          <div>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#cbd5e1' }}>
              {t('correlation.nextCorrelated')}
            </h3>
            <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: '#64748b' }}>
              {t('correlation.ofAllWith', { symptom: tSymptom(chain[chain.length - 1]) })}
            </p>
            
            {loading ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '40px',
                backgroundColor: '#0f172a',
                borderRadius: '8px',
                border: '1px solid #334155'
              }}>
                <span style={{ color: '#94a3b8' }}>⏳ {t('correlation.loading')}</span>
              </div>
            ) : error ? (
              <div style={{
                padding: '20px',
                backgroundColor: '#7f1d1d20',
                borderRadius: '8px',
                border: '1px solid #dc2626',
                color: '#fca5a5'
              }}>
                <strong>{t('correlation.errorLabel')}</strong> {error}
              </div>
            ) : suggestions.length === 0 ? (
              <div style={{
                padding: '20px',
                backgroundColor: '#0f172a',
                borderRadius: '8px',
                border: '1px solid #334155',
                color: '#94a3b8',
                textAlign: 'center'
              }}>
                {lastSymptom && lastSymptom.count < 10 
                  ? t('correlation.tooFewReports')
                  : t('correlation.noMoreCorrelated')
                }
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {suggestions.map((suggestion, index) => {
                  const rankColors = ['#fbbf24', '#9ca3af', '#cd7f32'];
                  const rankColor = rankColors[index] || '#6b7280';
                  
                  return (
                    <button
                      key={suggestion.symptom}
                      onClick={() => handleAddToChain(suggestion.symptom)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '16px',
                        backgroundColor: '#1e293b',
                        border: '1px solid #475569',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.2s',
                        width: '100%'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = '#334155';
                        e.currentTarget.style.borderColor = '#60a5fa';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = '#1e293b';
                        e.currentTarget.style.borderColor = '#475569';
                      }}
                    >
                      <span style={{
                        backgroundColor: rankColor,
                        color: index === 0 ? '#000' : '#fff',
                        padding: '4px 10px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        minWidth: '32px',
                        textAlign: 'center'
                      }}>
                        #{index + 1}
                      </span>
                      
                      <div style={{ flex: 1 }}>
                        <div style={{ 
                          color: '#e2e8f0', 
                          fontSize: '15px', 
                          fontWeight: '500',
                          marginBottom: '4px'
                        }}>
                          {tSymptom(suggestion.symptom)}
                        </div>
                        <div style={{ color: '#94a3b8', fontSize: '13px' }}>
                          {t('correlation.reports', { count: suggestion.count.toLocaleString() })}
                        </div>
                      </div>
                      
                      {/* Percentage with bar */}
                      <div style={{ width: '100px' }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          marginBottom: '4px'
                        }}>
                          <span style={{ 
                            color: suggestion.percentage >= 50 ? '#10b981' : 
                                   suggestion.percentage >= 25 ? '#f59e0b' : '#ef4444', 
                            fontSize: '15px', 
                            fontWeight: 'bold' 
                          }}>
                            {suggestion.percentage}%
                          </span>
                        </div>
                        <div style={{
                          height: '6px',
                          backgroundColor: '#374151',
                          borderRadius: '3px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            height: '100%',
                            width: `${Math.min(suggestion.percentage, 100)}%`,
                            backgroundColor: suggestion.percentage >= 50 ? '#10b981' : 
                                            suggestion.percentage >= 25 ? '#f59e0b' : '#ef4444',
                            borderRadius: '3px',
                            transition: 'width 0.3s'
                          }} />
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Info box */}
      {chain.length === 0 && (
        <div style={{
          marginTop: '20px',
          padding: '16px',
          backgroundColor: '#0f172a',
          borderRadius: '8px',
          border: '1px solid #334155'
        }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#cbd5e1' }}>
            💡 {t('correlation.infoTitle')}
          </h4>
          <p style={{ margin: 0, fontSize: '13px', color: '#94a3b8', lineHeight: '1.5' }}>
            {t('correlation.infoText')}
            <br/><br/>
            <em>{t('correlation.infoExample')}</em>
          </p>
        </div>
      )}
    </div>
  );
};

export default SymptomCorrelation;
